<template>
  <div class="srch_wrap" :class="{ open: open, on: open }" @keydown="onKeydown">
    <button
      ref="toggleBtnRef"
      class="btn_srch"
      :title="open ? '확장됨' : '축소됨'"
      @click="update(!open)"
    >
      {{ open ? '닫기' : '검색' }}
    </button>

    <div
      v-show="open"
      ref="panelRef"
      class="srch_wrap_box"
      role="dialog"
      aria-modal="true"
      aria-label="검색예약 서비스"
      @keydown="onKeydown"
    >
      <h2 class="tit">검색예약 서비스</h2>

      <div class="sky_box">
        <div class="area_srch">
          <div class="srch_word">
            <span class="word">
              <input
                ref="inputRef"
                v-model="searchKeyword"
                type="text"
                placeholder="검색어를 입력해 주세요."
                title="검색어를 입력해 주세요."
                maxlength="50"
                @focus="onFocus"
                @blur="onBlur"
                @keydown.enter.prevent="handleSearch"
              />
            </span>

            <button ref="searchBtnRef" type="button" class="btn_sh" @click="handleSearch">검색</button>

            <button
              ref="closeBtnRef"
              type="button"
              class="btn_close"
              tabindex="-1"
              :title="open ? '확장됨' : '축소됨'"
              @click="close"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, toRef, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useAlert } from '@/composables/useAlert'
const { showAlert } = useAlert()

const props = defineProps({ open: { type: Boolean, default: false } });
const emit = defineEmits(["update:open", "opened", "closed"]);

const router = useRouter();
const open = toRef(props, "open");

const panelRef = ref(null);
const toggleBtnRef = ref(null);
const inputRef = ref(null);
const searchBtnRef = ref(null);
const closeBtnRef = ref(null);

const searchKeyword = ref("");

function update(v) {
  emit("update:open", v);
  v ? emit("opened") : emit("closed");
}

function close() {
  update(false);
  // 닫힌 뒤에는 토글 버튼으로 포커스 복귀 (접근성 권장)
  nextTick(() => toggleBtnRef.value?.focus());
}

async function handleSearch() {
  const keyword = searchKeyword.value.trim();
  if (!keyword) {
    await showAlert("검색어를 입력해 주세요.");
    inputRef.value?.focus();
    return;
  }

  router.push({ name: "SearchResult", query: { keyword } });
  searchKeyword.value = "";
  close();
}

// 포커스 트랩: Tab/Shift+Tab이 오버레이 내부에서만 돌도록
function onKeydown(e) {
  if (!open.value) return;

  if (e.key === "Escape") {
    e.preventDefault();
    close();
    return;
  }
  if (e.key !== "Tab") return;

  // 원하는 탭 순서 고정: 토글 → input → 검색버튼
  const focusables = [
    toggleBtnRef.value,
    inputRef.value,
    searchBtnRef.value,
  ].filter(Boolean);

  if (!focusables.length) return;

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;

  // Shift+Tab: 첫 요소에서 뒤로 가면 마지막으로
  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
    return;
  }

  // Tab: 마지막 요소에서 앞으로 가면 첫 요소로(=토글)
  if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

function onFocus(e) {
  e.currentTarget.closest(".area_srch")?.classList.add("on", "over");
}

function onBlur(e) {
  const el = e.currentTarget;
  const area = el.closest(".area_srch");
  if (!area) return;
  area.classList.remove("over");
  area.classList.toggle("on", !!el.value);
}

// 열릴 때 인풋 포커스
watch(
  () => props.open,
  async (v) => {
    if (v) {
      await nextTick();
      inputRef.value?.focus();
    }
  },
  { immediate: true }
);
</script>


<style scoped>
/* 또는 검색 오버레이 영역만 한정 */
.srch_wrap .btn_close:focus-visible,
.srch_wrap .btn_sh:focus-visible,
.srch_wrap input:focus-visible {
  outline: 2px solid #000;
  outline-offset: 3px;
}
</style>